import Highcharts from "highcharts";
import Exporting from "highcharts/modules/exporting";
import OfflineExporting from "highcharts/modules/offline-exporting";

Exporting(Highcharts);
OfflineExporting(Highcharts);

/**
 * Genera un gráfico de Highcharts en un contenedor HTML.
 * @param {HTMLElement} container - Elemento HTML (ref) donde se renderiza el gráfico.
 * @param {Object} chartData - Objeto con datos del gráfico.
 * @param {String} chartType - Tipo de gráfico ("pie" o "column").
 * @param {String} countLabel - Sustantivo del conteo en el tooltip (QA-61):
 *   "Elementos" para infraestructura, "Personas" para población por región/comuna.
 */
export function renderGeoprocessingChart(container, chartData, chartType = "pie", countLabel = "Cantidad") {
  if (!container || !chartData) {
    console.warn("⚠️ No hay datos o contenedor para renderizar el gráfico");
    return;
  }

  try {
    // Detectar título dinámicamente (ej. "Total Buffer" o "Total Infra")
    const ignoredKeys = ["graphType", "availableLayers", "rawResults", "results_table", "filterGeometry", "filterGeometries", "population", "pdfReport"];
    const key = Object.keys(chartData).find(k => !ignoredKeys.includes(k));
    const bufferData = chartData[key] || [];
    const graphType = chartData.graphType || "InfluenciaRadial";

    // Metros redondos se muestran como km; el resto en metros con separador de miles
    const formatDistance = (m) =>
      m % 1000 === 0 ? `${(m / 1000).toLocaleString("es-CL")} km` : `${m.toLocaleString("es-CL")} m`;

    // 💡 Generar los datos para Highcharts
    let previousRadius = 0;
    const seriesData = bufferData.map(d => {
      let label;
      // el desglose por hora de la pluma (ver loadPopulationByRadius) manda
      // sobre el resto: distingue esta forma de fila sin importar el graphType
      if (d.hour != null) {
        label = `Hora ${d.hour}`;
      } else if (["InfluenciaPoligonal", "InfluenciaLineal", "PlumaDispersion"].includes(graphType)) {
        // estos geoprocesos agrupan por capa, así que el backend emite
        // "layer_name"; el resto emite "name"
        label = d.layer_name || "Capa sin nombre";
      } else if (d.radius) {
        // Los conteos del radial vienen por anillo EXCLUSIVO (el backend resta
        // los discos acumulados): la etiqueta "100000 m" se leía como "todo lo
        // que hay hasta 100 km" cuando la porción es solo el tramo 50-100 km.
        // Cada porción declara su tramo (QA-61).
        label = previousRadius
          ? `${formatDistance(previousRadius)} – ${formatDistance(d.radius)}`
          : `0 – ${formatDistance(d.radius)}`;
        previousRadius = d.radius;
      } else {
        label = d.name || "N/A";
      }
      return {
        name: label,
        y: d.features_count,
        percentage: d.percentage,
      };
    });

    const chartColors = [
      "#3b6e94",
      "#e8730c",
      "#2f7d5c",
      "#c98a1a",
      "#0e2a40",
      "#7b8f9f",
      "#b85c5c",
      "#5d7b6f",
    ];

    return Highcharts.chart(container, {
      colors: chartColors,
      chart: {
        type: chartType,
        backgroundColor: "transparent",
        style: {
          fontFamily: '"Public Sans", system-ui, sans-serif',
        },
        spacing: [12, 12, 8, 12],
      },
      accessibility: { enabled: false },
      exporting: { enabled: false },
      title: { text: null },
      // sin esto Highcharts etiqueta el eje X con índices (0,1,2…) en vez de point.name
      xAxis: { type: "category" },
      // la barra grafica la magnitud (point.y = features_count), no el
      // porcentaje; sin título el eje decía "Values"
      yAxis: { title: { text: countLabel } },
      tooltip: {
        useHTML: true,
        headerFormat: "",
        backgroundColor: "#ffffff",
        borderColor: "#dbe1e6",
        borderRadius: 6,
        shadow: {
          color: "rgba(14, 42, 64, 0.18)",
          offsetX: 0,
          offsetY: 4,
          opacity: 0.18,
          width: 8,
        },
        pointFormat:
          "<b>{point.name}</b><br>" + countLabel + ": <b>{point.y}</b><br>Porcentaje: <b>{point.percentage:.2f}%</b>",
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          borderColor: "#ffffff",
          borderWidth: 2,
          dataLabels: {
            enabled: true,
            // Sólo el porcentaje
            format: "{point.percentage:.1f}%",
            style: {
              color: "#2b3138",
              fontSize: "12px",
              fontWeight: "600",
              textOutline: "none",
            },
          },
          showInLegend: true,
        },
        column: {
          borderRadius: 3,
          colorByPoint: true,
          dataLabels: {
            enabled: true,
            format: "{point.y}",
            style: {
              color: "#2b3138",
              fontSize: "12px",
              fontWeight: "600",
              textOutline: "none",
            },
          },
          // Con una sola serie, la leyenda solo mostraba su nombre interno
          // ("Porcentaje") y hacer click ahí ocultaba todas las barras
          // (comportamiento nativo de Highcharts al ocultar una serie) — las
          // categorías ya se leen en el eje X, no aporta nada mostrarla.
          showInLegend: false,
        },
        series: { states: { inactive: { opacity: 0.5 } } },
      },
      legend: {
        enabled: true,
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          fontSize: "13px",
          color: "#2b3138",
          fontWeight: "500",
          cursor: "pointer",
        },
        itemHoverStyle: { color: "#3b6e94" },
      },
      credits: { enabled: false },
      series: [
        { name: "Porcentaje", colorByPoint: true, data: seriesData },
      ],
    });
  } catch (err) {
    console.error("❌ Error al renderizar gráfico:", err);
  }
}
